// src/pages/AdminPayments.tsx
//
// Admin console for Stripe payment configuration. Keys are stored in the DB
// (payment_settings) and read by the backend (DB first, env fallback). Secret
// values are write-only here — the API only ever returns a masked view.

import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { fetchPaymentSettings, savePaymentSettings, type PaymentSettings } from '../utils/creditsApi';

const label: React.CSSProperties = { display: 'block', marginBottom: '0.4rem', fontWeight: 500 };
const input: React.CSSProperties = {
  width: '100%', padding: '0.6rem 0.75rem', borderRadius: '6px', border: '1px solid #d1d5db', fontFamily: 'monospace',
};

const AdminPayments: React.FC = () => {
  const { token } = useAuth();
  const [settings, setSettings] = useState<PaymentSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const [publishableKey, setPublishableKey] = useState('');
  const [secretKey, setSecretKey] = useState('');
  const [webhookSecret, setWebhookSecret] = useState('');

  const load = async () => {
    if (!token) return;
    setLoading(true);
    const s = await fetchPaymentSettings(token);
    setSettings(s);
    setPublishableKey(s?.publishableKey || '');
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [token]);

  const handleSave = async () => {
    if (!token) return;
    setSaving(true);
    setToast(null);
    const body: { stripePublishableKey?: string; stripeSecretKey?: string; stripeWebhookSecret?: string } = {};
    if (publishableKey !== (settings?.publishableKey || '')) body.stripePublishableKey = publishableKey.trim();
    if (secretKey.trim()) body.stripeSecretKey = secretKey.trim();
    if (webhookSecret.trim()) body.stripeWebhookSecret = webhookSecret.trim();
    const s = await savePaymentSettings(token, body);
    if (s) {
      setSettings(s);
      setSecretKey('');
      setWebhookSecret('');
      setToast('Saved. Changes take effect within ~30 seconds.');
    } else {
      setToast('Failed to save. Please try again.');
    }
    setSaving(false);
  };

  if (loading) {
    return <div className="section container" style={{ padding: '4rem', textAlign: 'center' }}>Loading…</div>;
  }

  const available = settings?.available;

  return (
    <section className="section">
      <div className="container" style={{ maxWidth: '760px' }}>
        <h1>Payments</h1>
        <p className="text-secondary" style={{ marginBottom: '1.5rem' }}>
          Stripe keys that power credit-pack and subscription purchases. When these
          aren’t set, the Credits page shows an “unavailable” notice and the buy
          buttons are disabled.
        </p>

        <div
          className="card"
          style={{
            padding: '1rem 1.25rem', marginBottom: '1.5rem',
            backgroundColor: available ? '#dcfce7' : '#fef3c7',
            color: available ? '#166534' : '#92400e',
            border: `1px solid ${available ? '#bbf7d0' : '#fde68a'}`,
          }}
        >
          <strong>{available ? 'Purchasing is live' : 'Purchasing is disabled'}</strong>{' '}
          — {available
            ? 'Stripe is configured and accepting payments.'
            : 'Set a secret key and a webhook signing secret below to enable purchases.'}
        </div>

        {toast && (
          <div className="card" style={{ padding: '0.75rem 1rem', marginBottom: '1rem', backgroundColor: '#eff6ff', color: '#1e40af' }}>
            {toast}
          </div>
        )}

        <div className="card" style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
          <div>
            <label style={label}>Publishable key (pk_live… / pk_test…)</label>
            <input
              style={input}
              value={publishableKey}
              onChange={(e) => setPublishableKey(e.target.value)}
              placeholder="pk_live_…"
            />
            <p className="text-secondary" style={{ margin: '0.35rem 0 0', fontSize: '0.8rem' }}>
              Public — safe to expose in the browser.
            </p>
          </div>

          <div>
            <label style={label}>
              Secret key (sk_live… / sk_test…){' '}
              {settings?.secretKeySet && (
                <span style={{ color: '#166534', fontWeight: 400 }}>— set ({settings.secretKeyMasked})</span>
              )}
            </label>
            <input
              type="password"
              style={input}
              value={secretKey}
              onChange={(e) => setSecretKey(e.target.value)}
              placeholder={settings?.secretKeySet ? 'Enter a new key to replace' : 'sk_live_…'}
              autoComplete="off"
            />
            <p className="text-secondary" style={{ margin: '0.35rem 0 0', fontSize: '0.8rem' }}>
              Never shown after saving. Leave blank to keep the current key.
            </p>
          </div>

          <div>
            <label style={label}>
              Webhook signing secret (whsec…){' '}
              {settings?.webhookSecretSet && (
                <span style={{ color: '#166534', fontWeight: 400 }}>— set ({settings.webhookSecretMasked})</span>
              )}
            </label>
            <input
              type="password"
              style={input}
              value={webhookSecret}
              onChange={(e) => setWebhookSecret(e.target.value)}
              placeholder={settings?.webhookSecretSet ? 'Enter a new secret to replace' : 'whsec_…'}
              autoComplete="off"
            />
            <p className="text-secondary" style={{ margin: '0.35rem 0 0', fontSize: '0.8rem' }}>
              From your Stripe webhook endpoint. Required for credits/subscriptions to be granted after payment.
            </p>
          </div>

          <button className="btn btn-primary" onClick={handleSave} disabled={saving} style={{ alignSelf: 'flex-start' }}>
            {saving ? 'Saving…' : 'Save payment settings'}
          </button>
        </div>

        <div className="card" style={{ padding: '1.25rem 1.5rem', marginTop: '1.5rem', fontSize: '0.9rem' }}>
          <strong>Webhook setup</strong>
          <p className="text-secondary" style={{ margin: '0.5rem 0 0' }}>
            In Stripe → Developers → Webhooks, point an endpoint at{' '}
            <code>https://api.snowbirdindustries.us/api/stripe/webhook</code> and subscribe to
            <code> checkout.session.completed</code>, <code>invoice.paid</code>, and
            <code> customer.subscription.deleted</code>. Paste that endpoint’s signing secret above.
          </p>
        </div>
      </div>
    </section>
  );
};

export default AdminPayments;
